import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  createContext,
} from 'react';
import { sortBy } from 'lodash';
import { BuildJsonType, EnhancedAppointment } from '@bb/domain/dist/types';
import { utcToZonedTime } from 'date-fns-tz';
import { startOfDay, parseISO, isValid } from 'date-fns';

import { message } from 'components';
import { AppointmentController, FileController, ListingController } from 'api';
import errorReporter from '@app/errorReporter';
import { getDefaultListingTimezone } from 'api/ListingController';
import { GetListingFilesResponse } from 'api/FileController/list';
import { Listing } from 'api/ListingController/getListing';
import {
  TourListingAttachment,
  TourListingPhoto,
  TourItemContent,
  TourModalType,
} from './types';

type SelectedTimes = [Date | null, Date | null];

type TourListingContextType = {
  // Status Updates
  loading: boolean;
  // States
  modalType: TourModalType;
  setModalType: React.Dispatch<React.SetStateAction<TourModalType>>;
  setListingId: React.Dispatch<React.SetStateAction<string>>;
  setAppointmentId: React.Dispatch<React.SetStateAction<string>>;
  selectedTourItem: TourItemContent | null;
  setSelectedTourItem: React.Dispatch<
    React.SetStateAction<TourItemContent | null>
  >;
  selectedTimes: SelectedTimes;
  setSelectedTimes: React.Dispatch<React.SetStateAction<SelectedTimes>>;
  listingModalOpen: boolean;
  modalFromSearchBar: boolean;
  setListingModalState: (modalState: boolean, fromSearchBar?: boolean) => void;
  listing: Listing;
  photos: TourListingPhoto[];
  attachments: TourListingAttachment[];
  appointment: BuildJsonType<EnhancedAppointment>;
  // Functions
  resetSelectedTimes: () => void;
  mobileFooterRef: React.MutableRefObject<HTMLDivElement>;
};

const TourListingContext = createContext<TourListingContextType>(null);

export const TourListingContextProvider: React.FC = ({ children }) => {
  // Status Updates
  const [loading, setLoading] = useState(false);
  // States
  const [modalType, setModalType] = useState<TourModalType>(
    TourModalType.LISTING,
  );
  const [listingId, setListingId] = useState('');
  const [appointmentId, setAppointmentId] = useState('');

  const [selectedTourItem, setSelectedTourItem] =
    useState<TourItemContent>(null);
  const [selectedTimes, setSelectedTimes] = useState<SelectedTimes>([
    null,
    null,
  ]);

  const [listingModalOpen, setListingModalOpen] = useState(false);
  const [modalFromSearchBar, setModalFromSearchBar] = useState(false);

  const [listing, setListing] = useState<Listing>();
  const [photos, setPhotos] = useState<TourListingPhoto[]>([]);
  const [attachments, setAttachments] = useState<TourListingAttachment[]>([]);
  const [appointment, setAppointment] =
    useState<BuildJsonType<EnhancedAppointment>>();

  // Refs
  const mobileFooterRef = useRef(null);

  // Functions
  const setListingModalState = useCallback(
    (modalState: boolean, fromSearchBar = false) => {
      setListingModalOpen(modalState);
      setModalFromSearchBar(fromSearchBar);
    },
    [],
  );

  const formatListingFiles = (rawFiles: GetListingFilesResponse) => {
    const rawPhotos = sortBy(
      rawFiles.filter(file => file.isPhoto),
      ['order', 'name'],
    );
    const rawAttachments = sortBy(
      rawFiles.filter(file => file.isAttachment),
      'updatedAt',
    );

    const formattedPhotos = rawPhotos.map(rawPhoto => ({
      id: rawPhoto.id,
      url: rawPhoto.url,
      filename: rawPhoto.name,
    }));
    const formattedAttachments = rawAttachments.map(cur => ({
      id: cur.id,
      uid: cur.id,
      url: cur.url,
      mimetype: cur.filetype,
      filename: cur.name,
      size: cur.size,
    }));

    return { formattedPhotos, formattedAttachments };
  };

  const resetSelectedTimes = useCallback(() => {
    setSelectedTimes([
      selectedTourItem?.time || null,
      selectedTourItem?.timeEnd || null,
    ]);
  }, [selectedTourItem]);

  // Hooks
  useEffect(() => {
    const fetchListingData = async () => {
      setLoading(true);

      try {
        const [listingData, files] = await Promise.all([
          ListingController.getListing(listingId),
          FileController.list(listingId, 'listing'),
        ]);

        const { formattedPhotos, formattedAttachments } =
          formatListingFiles(files);

        if (!listingData.timezone) {
          listingData.timezone = await getDefaultListingTimezone({
            listingId,
          });
        }

        let formattedOpenHouses;
        if (listingData.openHouses && listingData.openHouses.length) {
          formattedOpenHouses = listingData.openHouses
            .filter(({ timeFrom, timeTo }) => timeFrom && timeTo)
            .map(openHouse => {
              // manual openhouses are not saved with date field
              const date = isValid(new Date(openHouse.date))
                ? parseISO(openHouse.date)
                : utcToZonedTime(
                    startOfDay(new Date(openHouse.timeFrom)),
                    listingData.timezone,
                  );

              return {
                ...openHouse,
                date: date.toISOString(),
                timeTo: utcToZonedTime(
                  openHouse.timeTo,
                  listingData.timezone,
                ).toISOString(),
                timeFrom: utcToZonedTime(
                  openHouse.timeFrom,
                  listingData.timezone,
                ).toISOString(),
              };
            });
        }

        const listingForState = {
          ...listingData,
          ...(formattedOpenHouses ? { openHouses: formattedOpenHouses } : {}),
        };

        setListing(listingForState);
        setPhotos(formattedPhotos);
        setAttachments(formattedAttachments);
      } catch (err) {
        message.error('Error fetching listing data');
        errorReporter.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (listingId) {
      fetchListingData();
    }
  }, [listingId]);

  useEffect(() => {
    const fetchAppointmentData = async () => {
      try {
        setLoading(true);
        const { appointment: appointmentData } =
          await AppointmentController.getAppointmentByIdForCoop(appointmentId);

        setAppointment(appointmentData);
      } catch (err) {
        message.error('Error fetching appointment data');
        errorReporter.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (appointmentId) {
      fetchAppointmentData();
    }
  }, [appointmentId]);

  useEffect(() => {
    resetSelectedTimes();
  }, [resetSelectedTimes]);

  return (
    <TourListingContext.Provider
      value={{
        // Status Updates
        loading,
        // States
        modalType,
        setModalType,
        setListingId,
        setAppointmentId,
        selectedTourItem,
        setSelectedTourItem,
        selectedTimes,
        setSelectedTimes,
        listingModalOpen,
        modalFromSearchBar,
        setListingModalState,
        listing,
        photos,
        attachments,
        appointment,
        // Refs
        mobileFooterRef,
        // Functions
        resetSelectedTimes,
      }}
    >
      {children}
    </TourListingContext.Provider>
  );
};

export default TourListingContext;
